import moment, { Moment } from "moment";
import { EventResetBatchQuery, EventResetBatchSummary, EventResetTripStatus } from "services/eventResetsService";

// Pure helpers for the Event Resets history page, kept apart so they can be tested without
// rendering the page.

export const DEFAULT_RANGE_DAYS = 7;
export const MAX_RANGE_DAYS = 90;
export const POLL_MS = 5000;

// The Event Resets history is an Admin-only tab on the review queue page; ?batch= opens one run.
export const EVENT_RESETS_TAB = "resets";
export const eventResetsLink = (batchId?: string | null) =>
  `/review-queue?tab=${EVENT_RESETS_TAB}` + (batchId ? `&batch=${batchId}` : "");

export interface EventResetFilters {
  from: Moment | null;
  to: Moment | null;
  status: string;
  source: string;
  userId: string;
  clientId: string;
  tripId: string;
}

export const defaultFilters = (now: Moment = moment()): EventResetFilters => ({
  from: now.clone().subtract(DEFAULT_RANGE_DAYS - 1, "days").startOf("day"),
  to: null,
  status: "",
  source: "",
  userId: "",
  clientId: "",
  tripId: "",
});

// The date pickers are local days; OrderApi wants a UTC [from, to) range. "to" is inclusive in the
// UI, so it is sent as the start of the following day.
export const toBatchQuery = (f: EventResetFilters, offset: number, pageSize: number): EventResetBatchQuery => {
  const tripId = Number(f.tripId.trim());
  return {
    from: f.from ? f.from.clone().startOf("day").toISOString() : null,
    to: f.to ? f.to.clone().add(1, "day").startOf("day").toISOString() : null,
    status: f.status || null,
    source: f.source || null,
    userId: f.userId ? Number(f.userId) : null,
    clientId: f.clientId ? Number(f.clientId) : null,
    tripId: f.tripId.trim() && Number.isFinite(tripId) && tripId > 0 ? tripId : null,
    offset,
    pageSize,
  };
};

// Why the filters can't be searched, or null when they can.
export const filtersError = (f: EventResetFilters): string | null => {
  if (f.tripId.trim() && !/^\d+$/.test(f.tripId.trim())) return "Trip # must be a number.";
  if (f.from && f.to && f.to.isBefore(f.from, "day")) return "The end date is before the start date.";
  if (f.from && (f.to ?? moment()).diff(f.from, "days") >= MAX_RANGE_DAYS)
    return `Pick a range of at most ${MAX_RANGE_DAYS} days.`;
  return null;
};

export const isActive = (b: EventResetBatchSummary) => b.status === "Running" || b.status === "Queued";

// Poll only while something on screen is still running.
export const shouldPoll = (batches: EventResetBatchSummary[]) => batches.some(isActive);

// One word for the batch row: running, or how it ended.
export const batchOutcome = (b: EventResetBatchSummary): { label: string; color: "info" | "success" | "warning" | "error" | "default" } => {
  if (isActive(b)) return { label: b.status, color: "info" };
  if (b.status === "Failed") return { label: "Failed", color: "error" };
  if (b.status === "Cancelled") return { label: "Cancelled", color: "default" };
  if (b.failedCount > 0) return { label: "Completed with failures", color: "warning" };
  return { label: "Succeeded", color: "success" };
};

export const doneCount = (b: EventResetBatchSummary) => Math.max(0, b.tripCount - b.pendingCount);

export const tripStatusColor = (s: EventResetTripStatus): "info" | "success" | "error" | "default" =>
  s === "Succeeded" ? "success" : s === "Failed" ? "error" : s === "Skipped" ? "default" : "info";

export const autoApproveText = (approved?: boolean | null, failReason?: string | null): string =>
  approved === true ? "Approved" : approved === false ? `Not approved${failReason ? `: ${failReason}` : ""}` : "-";
